/**
 * UnderwaterOverlay — WoW-style colour-grade when the camera is below water.
 *
 * Intentionally simple:
 *  - Blue-green tint that desaturates / reduces contrast (colour grade)
 *  - Slow sinusoidal UV warp for the "wavy" look
 *  - Soft vignette at screen edges
 *
 * The caller is responsible for also setting scene fog (FogExp2) to create
 * the limited-visibility / murky distance feel.
 */

import {
    Scene,
    Camera,
    Mesh,
    PlaneGeometry,
    ShaderMaterial,
} from 'three';

// Writes NDC directly — no matrix multiplication, no near/far clipping.
const vertexShader = /* glsl */`
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const fragmentShader = /* glsl */`
uniform float time;
varying vec2 vUv;

void main() {
    vec2 uv = vUv;

    // Slow sinusoidal warp — just enough to feel "underwater"
    uv.x += sin(uv.y * 6.0 + time * 0.8) * 0.004
          + sin(uv.y * 2.5 + time * 0.5) * 0.003;
    uv.y += cos(uv.x * 5.0 + time * 0.7) * 0.003;
    uv = clamp(uv, 0.0, 1.0);

    // Blue-green colour grade tint
    vec3 tint = vec3(0.03, 0.20, 0.44);

    // Soft vignette: slightly darker at edges
    vec2 c = vUv - 0.5;
    float vignette = 1.0 - dot(c * 1.5, c * 1.5);
    vignette = clamp(vignette, 0.0, 1.0);

    // Centre alpha ~0.45, edges up to ~0.62
    float alpha = mix(0.62, 0.45, vignette);

    gl_FragColor = vec4(tint, alpha);
}
`;

export class UnderwaterOverlay {
    private static _scene: Scene | null = null;
    private static _camera: Camera | null = null;
    private static _material: ShaderMaterial | null = null;

    static time: number = 0;
    static enabled: boolean = false;
    static waveSpeed: number = 0.5;

    static setWaveSpeed(v: number): void { UnderwaterOverlay.waveSpeed = v; }

    private static _init(): void {
        const mat = new ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: { time: { value: 0.0 } },
            transparent: true,
            depthTest: false,
            depthWrite: false,
        });
        const scene = new Scene();
        scene.add(new Mesh(new PlaneGeometry(2, 2), mat));
        this._scene    = scene;
        this._camera   = new Camera();  // dummy — vertex shader ignores all matrices
        this._material = mat;
    }

    static setEnabled(val: boolean): void {
        this.enabled = val;
    }

    static update(delta: number): void {
        if (!this.enabled) return;
        this.time += delta * UnderwaterOverlay.waveSpeed;
    }

    /** Call after the main scene render, before sprites, so UI stays on top. */
    static render(renderer: any): void {
        if (!this.enabled) return;
        if (!this._scene) this._init();
        this._material!.uniforms['time'].value = this.time;
        renderer.render(this._scene, this._camera);
    }
}
